import classNames from 'classnames';
import { ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { Autoplay, Navigation, Pagination } from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import { Swiper, SwiperProps } from 'swiper/react';
import { useCarouselPlaceholders } from '~/components/Carousel/Carousel.hooks';
import { CarouselNavButton } from '~/components/Carousel/Nav/CarouselNavButton';
import { CarouselPagination } from '~/components/Carousel/Pagination/CarouselPagination';
import styles from './Carousel.module.scss';
import './Swiper/Swiper.scss';

export type CarouselProps = Omit<Partial<SwiperProps>, 'placeholder'> & {
  placeholder?: ReactNode;
  shadow?: boolean;
};
const defaultProps: SwiperProps = {
  modules: [Autoplay, Pagination, Navigation],
};

export const Carousel = ({
  className,
  navigation,
  pagination,
  autoplay,
  children,
  placeholder,
  shadow,
  ...props
}: CarouselProps) => {
  const paginationRef = useRef(null);
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const swiperProps: SwiperProps = {
    pagination: pagination
      ? {
          el: paginationRef.current,
          clickable: true,
          ...(pagination as any),
        }
      : false,
    navigation: navigation
      ? {
          prevEl: prevRef.current,
          nextEl: nextRef.current,
          ...(navigation as any),
        }
      : false,
    autoplay,
  };
  const [mounted, setMount] = useState(false);
  const { childrenWithPlaceholders, update } = useCarouselPlaceholders({ children, placeholder });
  const visible = mounted;

  useLayoutEffect(() => {
    setMount(true);
  }, []);

  return (
    <div
      className={classNames(styles.root, className, {
        [styles.autoplay]: autoplay,
        [styles.navigation]: navigation,
        [styles.shadow]: shadow,
      })}>
      <div className={styles.body}>
        {visible && (
          <Swiper
            {...defaultProps}
            {...props}
            {...swiperProps}
            className={styles.swiper}
            onInit={update}
            onUpdate={update}
            onBreakpoint={update}>
            {childrenWithPlaceholders}
          </Swiper>
        )}
        {navigation && (
          <>
            <CarouselNavButton className={classNames(styles.prev, styles.button)} ref={prevRef} />
            <CarouselNavButton className={classNames(styles.next, styles.button)} ref={nextRef} />
          </>
        )}
      </div>
      <div className={styles.footer}>
        {pagination && <CarouselPagination className={styles.pagination} ref={paginationRef} />}
      </div>
    </div>
  );
};
